import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import {
  formatPacificCivilDateLong,
  formatPacificDateTime,
  pacificYmdFromIso,
} from '@/lib/adminPacificTime';
import type {
  AcquisitionFunnelDayRow,
  AcquisitionFunnelStep,
  AdminAcquisitionBackfillResponse,
  AdminAcquisitionFunnelsResponse,
} from '@/lib/types';

const DAYS_PER_PAGE = 7;
const SCROLL_COOLDOWN_MS = 1500;
const PAYWALL_LINE_COLOR = '#0284c7';
const PAID_LINE_COLOR = '#059669';

type ConversionChartPoint = {
  date: string;
  paywallRate: number;
  paidRate: number;
  cohortSize: number;
};

function formatPercent(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0%';
  if (value >= 0.995) return '100%';
  return `${Math.round(value * 1000) / 10}%`;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatAxisDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
}

function nicePercentAxisMax(value: number): number {
  if (value <= 0) return 0.25;
  const padded = value * 1.1;
  if (padded <= 0.1) return 0.1;
  if (padded <= 0.25) return 0.25;
  if (padded <= 0.5) return 0.5;
  if (padded <= 0.75) return 0.75;
  return 1;
}

function percentAxisTicks(max: number): number[] {
  const step =
    max <= 0.1 ? 0.02 : max <= 0.25 ? 0.05 : max <= 0.5 ? 0.1 : max <= 0.75 ? 0.25 : 0.25;
  const ticks: number[] = [];
  for (let v = 0; v <= max + step / 2; v += step) {
    ticks.push(Math.round(v * 1000) / 1000);
  }
  if ((ticks[ticks.length - 1] ?? 0) < max) ticks.push(max);
  return ticks;
}

function xLabelIndices(count: number): Set<number> {
  if (count <= 8) return new Set(Array.from({ length: count }, (_, i) => i));
  const target = Math.min(8, count);
  const indices = new Set<number>([0, count - 1]);
  for (let i = 1; i < target - 1; i += 1) {
    indices.add(Math.round((i * (count - 1)) / (target - 1)));
  }
  return indices;
}

function paywallStepForRow(row: AcquisitionFunnelDayRow): AcquisitionFunnelStep | undefined {
  return (
    row.steps.find((step) => step.event_key === 'Paywall Screen') ??
    row.steps[Math.max(row.steps.length - 3, 0)]
  );
}

function paidStepForRow(row: AcquisitionFunnelDayRow): AcquisitionFunnelStep | undefined {
  return row.steps[row.steps.length - 1];
}

function rowHasChartData(row: AcquisitionFunnelDayRow): boolean {
  if (row.pending !== true) return true;
  return row.cohort_size > 0 || row.steps.some((step) => step.count > 0);
}

function stepColor(index: number, total: number): string {
  const hue = 200 - (index / Math.max(total - 1, 1)) * 120;
  return `hsl(${hue}, 72%, 42%)`;
}

function FunnelStepCell({
  step,
  maxCount,
  index,
  totalSteps,
}: {
  step: AcquisitionFunnelStep;
  maxCount: number;
  index: number;
  totalSteps: number;
}) {
  const widthPct = maxCount > 0 ? Math.max((step.count / maxCount) * 100, 6) : 6;
  const color = stepColor(index, totalSteps);

  return (
    <div className='flex min-w-[132px] max-w-[132px] shrink-0 flex-col gap-1'>
      <p className='truncate text-[10px] font-medium text-gray-600' title={step.label}>
        {step.label}
      </p>
      <div className='h-8 overflow-hidden rounded bg-gray-100'>
        <div
          className='flex h-full items-center justify-center px-1'
          style={{ width: `${widthPct}%`, backgroundColor: color, minWidth: '2.5rem' }}
          title={`${step.label}: ${formatNumber(step.count)} (${formatPercent(step.rate_from_previous)} from previous)`}
        >
          <span className='text-[11px] font-semibold tabular-nums text-white'>
            {formatNumber(step.count)}
          </span>
        </div>
      </div>
      <p className='text-[10px] tabular-nums text-gray-400'>
        {index === 0 ? 'cohort' : formatPercent(step.rate_from_previous)}
      </p>
    </div>
  );
}

function DailyFunnelRow({
  row,
  stepLabels,
}: {
  row: AcquisitionFunnelDayRow;
  stepLabels: string[];
}) {
  const maxCount = Math.max(1, row.cohort_size, ...row.steps.map((step) => step.count));
  const paidStep = paidStepForRow(row);
  const landingStep = row.steps[0];
  const paywallStep = paywallStepForRow(row);
  const isPending = row.pending === true;

  return (
    <div
      className={`rounded-lg border bg-white p-4 shadow-sm ${
        isPending ? 'border-dashed border-gray-300 opacity-80' : 'border-gray-200'
      }`}
    >
      <div className='mb-3 flex flex-wrap items-end justify-between gap-2'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>
            {formatPacificCivilDateLong(row.date)}
            {isPending ? (
              <span className='ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500'>
                Loading…
              </span>
            ) : null}
          </p>
          <p className='text-sm font-bold tabular-nums text-sky-600'>
            {formatPercent(paywallStep?.rate_from_landing ?? 0)} paywall
          </p>
          <p className='text-xs text-gray-500'>
            Cohort {formatNumber(row.cohort_size)} landings
          </p>
        </div>
        <div className='text-right'>
          <p className='text-sm font-bold tabular-nums text-emerald-600'>
            {formatPercent(paidStep?.rate_from_landing ?? 0)} paid
          </p>
          <p className='text-xs tabular-nums text-gray-500'>
            {formatNumber(landingStep?.count ?? 0)} → {formatNumber(paidStep?.count ?? 0)}
          </p>
        </div>
      </div>
      <div className='overflow-x-auto pb-1'>
        <div className='flex min-w-max gap-2'>
          {row.steps.map((step, index) => (
            <FunnelStepCell
              key={`${row.date}-${stepLabels[index] ?? step.event_key}`}
              step={step}
              maxCount={maxCount}
              index={index}
              totalSteps={row.steps.length}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ConversionRateChart({ points }: { points: ConversionChartPoint[] }) {
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Callback ref: the chart shell is not mounted while `points` is empty
  // (early return below), so a mount-only useEffect would see chartRef=null
  // and never attach ResizeObserver after data arrives — leaving chartWidth
  // at 0 and the plot area blank forever.
  const chartRef = useCallback((el: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (!el) return;

    const updateWidth = () => {
      const next = el.clientWidth;
      if (next > 0) setChartWidth(next);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    resizeObserverRef.current = observer;
  }, []);

  const layout = useMemo(() => {
    if (!points.length || chartWidth <= 0) return null;

    const n = points.length;
    const padLeft = 48;
    const padRight = 16;
    const padTop = 20;
    const padBottom = 36;
    const width = chartWidth;
    const height = 240;
    const innerW = width - padLeft - padRight;
    const innerH = height - padTop - padBottom;
    const maxRate = Math.max(
      ...points.map((point) => Math.max(point.paywallRate, point.paidRate)),
      0,
    );
    const axisMax = nicePercentAxisMax(maxRate);
    const ticks = percentAxisTicks(axisMax);
    const labelIndices = xLabelIndices(n);

    const mapped = points.map((point, i) => {
      const x = n <= 1 ? padLeft + innerW / 2 : padLeft + (i / (n - 1)) * innerW;
      const paywallY =
        padTop + innerH - (axisMax > 0 ? (point.paywallRate / axisMax) * innerH : 0);
      const paidY =
        padTop + innerH - (axisMax > 0 ? (point.paidRate / axisMax) * innerH : 0);
      return { ...point, x, paywallY, paidY, index: i };
    });

    const paywallPath = mapped
      .map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x} ${point.paywallY}`)
      .join(' ');
    const paidPath = mapped
      .map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x} ${point.paidY}`)
      .join(' ');

    return {
      n,
      width,
      height,
      padLeft,
      padTop,
      padBottom,
      innerW,
      innerH,
      axisMax,
      ticks,
      labelIndices,
      mapped,
      paywallPath,
      paidPath,
    };
  }, [points, chartWidth]);

  if (!points.length) {
    return null;
  }

  const hovered = hoveredIndex !== null && layout ? layout.mapped[hoveredIndex] : null;

  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <div className='mb-3 flex flex-wrap items-baseline justify-between gap-2'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>Conversion rates</p>
          <p className='text-xs text-gray-500'>
            Landing → paywall and landing → paid by Pacific cohort day
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3 text-xs font-medium'>
          <span className='inline-flex items-center gap-1.5 text-sky-700'>
            <span
              className='inline-block h-2 w-2 rounded-full'
              style={{ backgroundColor: PAYWALL_LINE_COLOR }}
            />
            Paywall
          </span>
          <span className='inline-flex items-center gap-1.5 text-emerald-700'>
            <span
              className='inline-block h-2 w-2 rounded-full'
              style={{ backgroundColor: PAID_LINE_COLOR }}
            />
            Paid
          </span>
        </div>
      </div>

      <div
        ref={chartRef}
        className='rounded-md border border-gray-100 bg-gray-50/50'
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {!layout ? (
          <div className='h-[240px]' />
        ) : (
          <svg
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            width='100%'
            height={layout.height}
            role='img'
            aria-label='Paywall and paid conversion rate line chart'
          >
            <text
              x={14}
              y={layout.padTop + layout.innerH / 2}
              transform={`rotate(-90 14 ${layout.padTop + layout.innerH / 2})`}
              textAnchor='middle'
              className='fill-gray-500 text-[11px] font-medium'
            >
              Conversion
            </text>

            {layout.ticks.map((tick) => {
              const y =
                layout.padTop +
                layout.innerH -
                (layout.axisMax > 0 ? (tick / layout.axisMax) * layout.innerH : 0);
              return (
                <g key={tick}>
                  <line
                    x1={layout.padLeft}
                    y1={y}
                    x2={layout.padLeft + layout.innerW}
                    y2={y}
                    stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                    strokeDasharray={tick === 0 ? undefined : '4 4'}
                  />
                  <text
                    x={layout.padLeft - 8}
                    y={y + 4}
                    textAnchor='end'
                    className='fill-gray-500 text-[10px] tabular-nums'
                  >
                    {formatPercent(tick)}
                  </text>
                </g>
              );
            })}

            {layout.n > 1 ? (
              <>
                <path
                  d={layout.paywallPath}
                  fill='none'
                  stroke={PAYWALL_LINE_COLOR}
                  strokeWidth={2.5}
                  strokeLinejoin='round'
                  strokeLinecap='round'
                  className='pointer-events-none'
                />
                <path
                  d={layout.paidPath}
                  fill='none'
                  stroke={PAID_LINE_COLOR}
                  strokeWidth={2.5}
                  strokeLinejoin='round'
                  strokeLinecap='round'
                  className='pointer-events-none'
                />
              </>
            ) : null}

            {layout.mapped.map((point) => {
              const isHovered = hoveredIndex === point.index;
              const showLabel = layout.labelIndices.has(point.index);
              const slotLeft =
                layout.n <= 1
                  ? layout.padLeft
                  : layout.padLeft +
                    ((point.index - 0.5) / Math.max(layout.n - 1, 1)) * layout.innerW;
              const slotRight =
                layout.n <= 1
                  ? layout.padLeft + layout.innerW
                  : layout.padLeft +
                    ((point.index + 0.5) / Math.max(layout.n - 1, 1)) * layout.innerW;
              const slotX = Math.max(layout.padLeft, slotLeft);
              const slotW = Math.max(
                8,
                Math.min(layout.padLeft + layout.innerW, slotRight) - slotX,
              );

              return (
                <g key={point.date}>
                  <rect
                    x={slotX}
                    y={layout.padTop}
                    width={slotW}
                    height={layout.innerH}
                    fill='transparent'
                    className='cursor-pointer'
                    onMouseEnter={() => setHoveredIndex(point.index)}
                  />
                  <circle
                    cx={point.x}
                    cy={point.paywallY}
                    r={isHovered ? 5 : 3.5}
                    fill={isHovered ? PAYWALL_LINE_COLOR : '#ffffff'}
                    stroke={PAYWALL_LINE_COLOR}
                    strokeWidth={isHovered ? 0 : 2}
                    className='pointer-events-none'
                    opacity={hoveredIndex === null || isHovered ? 1 : 0.4}
                  />
                  <circle
                    cx={point.x}
                    cy={point.paidY}
                    r={isHovered ? 5 : 3.5}
                    fill={isHovered ? PAID_LINE_COLOR : '#ffffff'}
                    stroke={PAID_LINE_COLOR}
                    strokeWidth={isHovered ? 0 : 2}
                    className='pointer-events-none'
                    opacity={hoveredIndex === null || isHovered ? 1 : 0.4}
                  />
                  {showLabel ? (
                    <text
                      x={point.x}
                      y={layout.height - 12}
                      textAnchor='middle'
                      className='fill-gray-600 text-[10px] pointer-events-none'
                    >
                      {formatAxisDate(point.date)}
                    </text>
                  ) : null}
                </g>
              );
            })}

            {hovered ? (
              <g pointerEvents='none'>
                <line
                  x1={hovered.x}
                  y1={layout.padTop}
                  x2={hovered.x}
                  y2={layout.padTop + layout.innerH}
                  stroke='#9ca3af'
                  strokeOpacity={0.5}
                  strokeWidth={1}
                />
                {(() => {
                  const lines = [
                    formatPacificCivilDateLong(hovered.date),
                    `Paywall ${formatPercent(hovered.paywallRate)}`,
                    `Paid ${formatPercent(hovered.paidRate)}`,
                    `Cohort ${formatNumber(hovered.cohortSize)}`,
                  ];
                  const tooltipW = Math.max(148, ...lines.map((line) => line.length * 6.4 + 20));
                  const tooltipH = 72;
                  const tooltipX = Math.min(
                    Math.max(hovered.x - tooltipW / 2, layout.padLeft),
                    layout.padLeft + layout.innerW - tooltipW,
                  );
                  const aboveY = Math.min(hovered.paywallY, hovered.paidY) - tooltipH - 10;
                  const tooltipY =
                    aboveY >= layout.padTop
                      ? aboveY
                      : Math.max(hovered.paywallY, hovered.paidY) + 10;

                  return (
                    <>
                      <rect
                        x={tooltipX}
                        y={tooltipY}
                        width={tooltipW}
                        height={tooltipH}
                        rx={6}
                        fill='#111827'
                        opacity={0.92}
                      />
                      {lines.map((line, i) => (
                        <text
                          key={line}
                          x={tooltipX + 10}
                          y={tooltipY + 16 + i * 14}
                          className={`fill-white text-[11px] ${
                            i === 0 ? 'font-semibold' : 'tabular-nums'
                          }`}
                        >
                          {line}
                        </text>
                      ))}
                    </>
                  );
                })()}
              </g>
            ) : null}
          </svg>
        )}
      </div>
    </div>
  );
}

function BackfillProgress({
  complete,
  total,
  pending,
  rateLimitedUntil,
}: {
  complete: number;
  total: number;
  pending: number;
  rateLimitedUntil: string | null;
}) {
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;

  return (
    <div className='rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700'>
      <div className='mb-1 flex flex-wrap items-center justify-between gap-2'>
        <span>
          Cached <span className='font-semibold tabular-nums'>{complete}</span> of{' '}
          <span className='tabular-nums'>{total}</span> days ({pct}%)
        </span>
        {pending > 0 ? (
          <span className='text-gray-500'>
            <span className='tabular-nums'>{pending}</span> queued for backfill
          </span>
        ) : null}
      </div>
      <div className='h-1.5 overflow-hidden rounded-full bg-gray-100'>
        <div className='h-full rounded-full bg-sky-500 transition-all' style={{ width: `${pct}%` }} />
      </div>
      {rateLimitedUntil ? (
        <p className='mt-1 text-[11px] text-amber-700'>
          Amplitude rate limited until {formatPacificDateTime(rateLimitedUntil)} (
          {pacificYmdFromIso(rateLimitedUntil)} Pacific)
        </p>
      ) : null}
    </div>
  );
}

function AdminAcquisitionPage() {
  const { getToken } = useAuth();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const lastScrollFetchAtRef = useRef(0);

  const funnelsQuery = useInfiniteQuery({
    queryKey: ['admin', 'acquisition', 'funnels'],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        days: String(DAYS_PER_PAGE),
      });
      if (pageParam) {
        params.set('end_date', pageParam);
      }
      return apiJson<AdminAcquisitionFunnelsResponse>(
        getToken,
        `/v1/admin/acquisition/funnels?${params.toString()}`,
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more && lastPage.next_end_date ? lastPage.next_end_date : null,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      const hasPendingRows = pages.some((page) => page.rows.some((row) => row.pending));
      const backfillPending = pages[0]?.backfill.pending ?? 0;
      return hasPendingRows || backfillPending > 0 ? 15000 : false;
    },
  });

  const backfillStatusQuery = useQuery({
    queryKey: ['admin', 'acquisition', 'backfill'],
    queryFn: () =>
      apiJson<AdminAcquisitionBackfillResponse>(getToken, '/v1/admin/acquisition/backfill'),
    refetchInterval: (query) => {
      const pending = query.state.data?.backfill.pending ?? 0;
      return pending > 0 ? 15000 : 60000;
    },
  });

  const backfillMutation = useMutation({
    mutationFn: () =>
      apiJson<AdminAcquisitionBackfillResponse>(
        getToken,
        '/v1/admin/acquisition/backfill?full_history=true',
        { method: 'POST' },
      ),
    onSuccess: () => {
      void funnelsQuery.refetch();
      void backfillStatusQuery.refetch();
    },
  });

  const pages = funnelsQuery.data?.pages ?? [];
  const rows = useMemo(() => pages.flatMap((page) => page.rows), [pages]);
  const stepLabels = pages[0]?.step_labels ?? [];
  const amplitudeError = pages.find((page) => page.amplitude_error)?.amplitude_error ?? null;
  const amplitudeStaleAt =
    pages.find((page) => page.amplitude_stale_at)?.amplitude_stale_at ?? null;
  const backfill =
    backfillStatusQuery.data?.backfill ??
    pages[pages.length - 1]?.backfill ?? {
      pending: 0,
      complete: 0,
      total: 0,
      rate_limited_until: null,
    };

  const conversionChartPoints = useMemo((): ConversionChartPoint[] => {
    const usable = rows.filter(rowHasChartData);
    return [...usable].reverse().map((row) => {
      const paywallStep = paywallStepForRow(row);
      const paidStep = paidStepForRow(row);
      return {
        date: row.date,
        paywallRate: paywallStep?.rate_from_landing ?? 0,
        paidRate: paidStep?.rate_from_landing ?? 0,
        cohortSize: row.cohort_size,
      };
    });
  }, [rows]);

  const handleLoadMore = useCallback(() => {
    const now = Date.now();
    if (now - lastScrollFetchAtRef.current < SCROLL_COOLDOWN_MS) {
      return;
    }
    if (
      funnelsQuery.hasNextPage &&
      !funnelsQuery.isFetchingNextPage &&
      !funnelsQuery.isFetching
    ) {
      lastScrollFetchAtRef.current = now;
      void funnelsQuery.fetchNextPage();
    }
  }, [funnelsQuery]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: '240px 0px', threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [handleLoadMore, rows.length]);

  const isFetchingFunnels = funnelsQuery.isFetching && !funnelsQuery.isFetchingNextPage;
  const hasPendingRows = rows.some((row) => row.pending === true);
  // Keep a small header spinner for in-flight funnel fetches / pending day rows —
  // never replace the page body with a full-screen gate.
  const showTopSpinner = isFetchingFunnels || hasPendingRows;

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <div className='flex items-center gap-2'>
            <h1 className='text-2xl font-bold text-gray-900'>Acquisition</h1>
            {showTopSpinner ? (
              <div
                className='h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent'
                title='Loading or refreshing funnel data…'
                aria-label='Loading or refreshing funnel data'
              />
            ) : null}
          </div>
          <p className='text-sm text-gray-500'>
            Mobile onboarding funnels by Pacific day — Landing Screen through Subscription Paid.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <button
            type='button'
            onClick={() => backfillMutation.mutate()}
            disabled={backfillMutation.isPending}
            className='inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60'
          >
            Backfill history
          </button>
          <button
            type='button'
            onClick={() => funnelsQuery.refetch()}
            disabled={funnelsQuery.isFetching}
            className='inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60'
          >
            <FiRefreshCw className={funnelsQuery.isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className='rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900'>
        Each row is a cohort that opened the app on that day (Landing Screen). Steps count users
        who reached each screen after landing within a 30-day window. Historical days are cached in
        Redis and backfilled slowly to stay under Amplitude rate limits.
      </div>

      <BackfillProgress
        complete={backfill.complete}
        total={backfill.total}
        pending={backfill.pending}
        rateLimitedUntil={backfill.rate_limited_until}
      />

      <ConversionRateChart points={conversionChartPoints} />

      {amplitudeError ? (
        <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
          {amplitudeStaleAt ? (
            <>
              <span className='font-semibold'>Showing cached Amplitude data.</span> {amplitudeError}{' '}
              Cached at {formatPacificDateTime(amplitudeStaleAt)} (
              {pacificYmdFromIso(amplitudeStaleAt)} Pacific).
            </>
          ) : (
            <>
              <span className='font-semibold'>Amplitude export unavailable.</span> {amplitudeError}
            </>
          )}
        </div>
      ) : null}

      {!funnelsQuery.isLoading && rows.length === 0 ? (
        <p className='text-sm text-gray-500'>No funnel data available yet.</p>
      ) : null}

      <div className='space-y-4'>
        {rows.map((row) => (
          <DailyFunnelRow key={row.date} row={row} stepLabels={stepLabels} />
        ))}
      </div>

      <div ref={loadMoreRef} className='flex min-h-12 items-center justify-center py-4'>
        {funnelsQuery.isFetchingNextPage ? (
          <div className='h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        ) : funnelsQuery.hasNextPage ? (
          <p className='text-xs text-gray-400'>Scroll for earlier days…</p>
        ) : rows.length > 0 ? (
          <p className='text-xs text-gray-400'>End of available history.</p>
        ) : null}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/acquisition')({
  component: AdminAcquisitionPage,
});
